import { computed, nextTick, ref, watch } from "vue";
import { useAuthStore } from "@/stores/authStore";
import { useSnackStore } from "@/stores/snackStore";
import { useSettingsStore, SETTINGS_DEFAULTS } from "@/stores/settingsStore";
import PracticeStaffSettingsService, {
  type TPracticeStaffUserSettings,
  EPracticeStaffSettingsKey,
} from "@/api/models/practice/practiceStaffSettings";
import { useI18n } from "vue-i18n";
import { detectBrowserTimeZone } from "@/utils/browserTimeZone";
import { djangoViewsetErrorHandler } from "@/utils/DjangoError";

// lastKnownTZ is not a store setting – see buildPreferencesPayload
type PreferenceKey = Exclude<EPracticeStaffSettingsKey, EPracticeStaffSettingsKey.LAST_KNOWN_TZ>;

const SILENT_PREFERENCE_KEYS = new Set<PreferenceKey>([
  EPracticeStaffSettingsKey.LAST_REFERRAL_PRACTICE,
  EPracticeStaffSettingsKey.DASHBOARD_TOUR_COMPLETED,
  EPracticeStaffSettingsKey.OFFICES_CONTRACTS_TOUR_COMPLETED,
  EPracticeStaffSettingsKey.OFFICE_BILLING_TOUR_COMPLETED,
  EPracticeStaffSettingsKey.TUTORIAL_DIALOG_SEEN,
]);

const PREFERENCE_KEYS: PreferenceKey[] = [
  EPracticeStaffSettingsKey.IS_DARK_MODE,
  EPracticeStaffSettingsKey.NAME_FORMAT_SETTING,
  EPracticeStaffSettingsKey.LANGUAGE,
  EPracticeStaffSettingsKey.ADMINISTRATIVE_ASSISTANT_HINTS_DISABLED,
  EPracticeStaffSettingsKey.CHAT_ASSISTANT_HINTS_DISABLED,
  EPracticeStaffSettingsKey.SETTINGS_MENU_COLLAPSED,
  EPracticeStaffSettingsKey.CHAT_DEFAULT_TAB,
  EPracticeStaffSettingsKey.CHAT_ENTER_BEHAVIOR,
  EPracticeStaffSettingsKey.CHAT_SYSTEM_MSGS,
  EPracticeStaffSettingsKey.ANNO_CONTROL,
  EPracticeStaffSettingsKey.DATE_FORMAT_INPUT,
  EPracticeStaffSettingsKey.DATE_FORMAT_DISPLAY,
  EPracticeStaffSettingsKey.HOUR_FORMAT_DISPLAY,
  EPracticeStaffSettingsKey.SCHEDULE_TIMELINE,
  EPracticeStaffSettingsKey.DASHBOARD_TOUR_COMPLETED,
  EPracticeStaffSettingsKey.OFFICES_CONTRACTS_TOUR_COMPLETED,
  EPracticeStaffSettingsKey.OFFICE_BILLING_TOUR_COMPLETED,
  EPracticeStaffSettingsKey.TUTORIAL_DIALOG_SEEN,
  EPracticeStaffSettingsKey.DASHBOARD_DEFAULT_TAB,
  EPracticeStaffSettingsKey.LAST_REFERRAL_PRACTICE,
  EPracticeStaffSettingsKey.SKIP_CONFIRMATION_DIALOGS,
  EPracticeStaffSettingsKey.FORM_CUSTOMIZATION_BETA,
];

export const usePracticeStaffSettings = ({ setupWatchers = true } = {}) => {
  const authStore = useAuthStore();
  const snackStore = useSnackStore();
  const settingsStore = useSettingsStore();
  const { t } = useI18n();
  const staffId = computed(() => authStore.authUser?.logged_staff.id);
  const isApplyingFromServer = ref(false);

  const staffUserSettings = computed((): TPracticeStaffUserSettings | null => {
    const value = authStore.authUser?.logged_staff?.user_settings;
    if (!value) return null;
    return value;
  });

  const storedLastKnownTimeZone = computed(
    () => staffUserSettings.value?.prefferences_v1?.[EPracticeStaffSettingsKey.LAST_KNOWN_TZ]
  );

  const buildPreferencesPayload = (lastKnownTimeZone?: string): TPracticeStaffUserSettings => {
    const prefferences_v1 = {} as NonNullable<TPracticeStaffUserSettings["prefferences_v1"]>;

    PREFERENCE_KEYS.forEach((key) => {
      (prefferences_v1[key] as unknown) = settingsStore[key];
    });

    // Not a store setting – carry it over from the server so a settings save doesn't drop it
    const timeZone = lastKnownTimeZone ?? storedLastKnownTimeZone.value;
    if (timeZone) {
      prefferences_v1[EPracticeStaffSettingsKey.LAST_KNOWN_TZ] = timeZone;
    }

    return { prefferences_v1 };
  };

  const applyPreferencesToStore = (data: TPracticeStaffUserSettings | null | undefined) => {
    if (!data || typeof data !== "object") return;

    const prefs = data.prefferences_v1;
    if (!prefs || typeof prefs !== "object") return;

    isApplyingFromServer.value = true;
    PREFERENCE_KEYS.forEach((key) => {
      const value = prefs[key] ?? SETTINGS_DEFAULTS[key as keyof typeof SETTINGS_DEFAULTS];
      const settingsKey = key as keyof typeof settingsStore;
      (settingsStore as any)[settingsKey] = value;
    });
    void nextTick(() => {
      isApplyingFromServer.value = false;
    });
  };

  const saveStaffSettings = async (successMessage?: string | null, lastKnownTimeZone?: string) => {
    try {
      const payload = buildPreferencesPayload(lastKnownTimeZone);
      const response = await PracticeStaffSettingsService.save({}, payload);

      // Keep authStore.logged_staff.user_settings in sync within current session
      authStore.$patch((state) => {
        if (state.authUser?.logged_staff) {
          state.authUser.logged_staff.user_settings = response.data;
        }
      });
      if (successMessage !== null) {
        snackStore.showSuccess(
          successMessage ??
            t("generic.messages.success.edit", { entity: t("generic.entity.settings") })
        );
      }
    } catch (error) {
      logError(error, {
        context: {
          feature: "practice-staff-settings",
          step: "saveStaffSettings",
          staffId: staffId.value,
        },
      });
      djangoViewsetErrorHandler(snackStore)(error);
    }
  };

  // BE renders emails in this zone when the time zone preference is "auto"
  const syncLastKnownTimeZone = () => {
    if (!staffId.value) return;
    const detected = detectBrowserTimeZone();
    if (!detected || detected === storedLastKnownTimeZone.value) return;

    void saveStaffSettings(null, detected);
  };

  if (setupWatchers) {
    // Apply staff preferences whenever user_settings change (eg. after setMe / login)
    watch(
      staffUserSettings,
      (value) => {
        applyPreferencesToStore(value);
      },
      { immediate: true }
    );

    PREFERENCE_KEYS.forEach((key) => {
      watch(
        () => settingsStore[key],
        () => {
          if (isApplyingFromServer.value) return;
          if (settingsStore.isBackfillingPreferencesFromAuth) return;
          if (!staffId.value) return;

          void saveStaffSettings(SILENT_PREFERENCE_KEYS.has(key) ? null : undefined);
        }
      );
    });

    // After login only: on a reload the persisted authStore already holds the staff,
    // and its user_settings may be stale until setMe refreshes them
    watch(staffId, syncLastKnownTimeZone);
  }

  return {
    loadStaffSettings: () => applyPreferencesToStore(staffUserSettings.value),
    saveStaffSettings,
  };
};
